<template>
	<Col v-if="!isLoading" class="node-credentials-suggestion-container width-100">
		<Row @click.native="isOpen = !isOpen" class="flex-align-center activator width-100 flex-justify-space-between px-4">
			<Row class="flex-align-center">
				<Col class="integration-icon-card flex-centerize">
					<SIcon :name="serviceSlug" width="12" height="12" />
				</Col>
				<Row class="step-title">{{ stepTitle }}</Row>
				<v-icon :class="{ rotate: isOpen }" size="14" color="#64666A" class="ml-0">mdi-chevron-right</v-icon>
			</Row>
			<Row @click.native.prevent="" @click.native.stop="">
				<AnemicButton @click="chooseOneForAll" tile :class="{ selected: !acceptSuggestion }" class="pa-0 left-button">
					One for all
				</AnemicButton>
				<AnemicButton @click="choosePerUser" tile :class="{ selected: acceptSuggestion }" class="pa-0 right-button">Per user</AnemicButton>
			</Row>
		</Row>
		<v-expand-transition>
			<NodeSuggestionContentDetails :connectionNames="connectionNames" v-if="isOpen" class="pa-4" />
		</v-expand-transition>
	</Col>
</template>

<script>
import { shortenText } from '@/utils/language-service';
import { getNode } from '@/store/modules/nodes/getters';
import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';
import { getNodeData } from '@/store/modules/nodes/actions';
import { getAction } from '@/store/modules/actions/getters';
import { getActionsOfApp } from '@/store/modules/apps/actions';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import NodeSuggestionContentDetails from '@/components/dialogs/SuggestCredentialsTypeDialog/NodeSuggestionContentDetails.vue';
export default {
	name: 'NodeCredentialsSuggestion',
	components: {
		NodeSuggestionContentDetails,
	},
	props: {
		widgetData: { type: Array },
		isLoading: { type: Boolean },
		isFromPublish: { type: Boolean },
		acceptSuggestion: { type: Boolean },
		nodeId: { type: Number, required: true },
	},
	computed: {
		...mapGettersShorthand({ useNode: getNode, useAction: getAction, useAccountConnection: getAccountConnection }),
		node() {
			return this.useNode(this.nodeId) || {};
		},
		isWidgetNode() {
			return this.serviceSlug === ServiceSlugs.Widgets;
		},
		actionId() {
			return this.node.action;
		},
		action() {
			return this.useAction(this.actionId) || {};
		},
		serviceSlug() {
			return this.node.service_slug;
		},
		stepTitle() {
			return this.node.node_title || this.action.title;
		},
		connections() {
			let credentials = [this.nodeCredentials];
			if (this.isWidgetNode) {
				credentials = this.widgetCredentials;
			}
			return credentials.map((c) => this.useAccountConnection(c));
		},
		widgetCredentials() {
			return this.widgetData.map((field) => field.credentials_id);
		},
		nodeCredentials() {
			return this.node.credentials_configuration?.credentials;
		},
		connectionNames() {
			return this.connections.map((c) => shortenText(c?.connection_name, 45));
		},
	},
	methods: {
		...mapActionsShorthand({ getNodeData, getActionsOfApp }),
		updateAcceptSuggestion(acceptSuggestion) {
			this.$emit('update-accept-suggestion', { nodeId: this.nodeId, acceptSuggestion });
		},
		updateLoadingState(isLoading) {
			this.$emit('update-node-loading-state', { nodeId: this.nodeId, isLoading });
		},
		choosePerUser() {
			this.updateAcceptSuggestion(true);
		},
		chooseOneForAll() {
			this.updateAcceptSuggestion(false);
		},
	},
	async created() {
		this.updateLoadingState(true);
		await this.getNodeData({ id: this.nodeId, published: !this.isFromPublish });
		await this.getActionsOfApp({ id: this.node.service, version: this.node.service_version });
		this.updateLoadingState(false);
	},
	data() {
		return {
			isOpen: false,
		};
	},
};
</script>

<style scoped lang="scss">
.node-credentials-suggestion-container {
	border-radius: 13px;
	background: var(--white);
	border: 1px solid #e9edf3;
	transition: var(--transition-time);
	&:hover {
		transition: var(--transition-time);
		box-shadow: 0px 2px 4px var(--shadow-color);
	}
}
.integration-icon-card {
	width: 26px;
	height: 26px;
	border-radius: 7px;
	margin-right: 14px;
	background: var(--white);
	border: 1px solid var(--border-color);
	box-shadow: 0px 1px 2px var(--border-color);
}
.step-title {
	font-size: 13px;
	font-weight: medium;
	color: var(--primary-font-color);
}

.recommendation-button {
	width: 83px !important;
	height: 20px !important;
	opacity: 0.5 !important;
	font-size: 12px !important;
	color: #262626 !important;
	transition: 0.1s !important;
	letter-spacing: 0px !important;
	font-weight: normal !important;
	text-transform: none !important;
	background: var(--white) !important;
	border: 1px solid #d6ddea !important;
}

.selected-recommendation-button {
	$active-color: #5f8ff1;
	opacity: 1 !important;
	transition: 0.1s !important;
	color: $active-color !important;
	background: #f8faff !important;
	border: 1px solid $active-color !important;
}

.left-button {
	@extend .recommendation-button;
	border-radius: 6px 0px 0px 6px;
	&.selected {
		@extend .selected-recommendation-button;
	}
	:not(.selected) {
		border-right: none !important;
	}
}

.right-button {
	@extend .recommendation-button;
	border-radius: 0px 6px 6px 0px;
	&.selected {
		@extend .selected-recommendation-button;
	}
	:not(.selected) {
		border-left: none !important;
	}
}
.activator {
	cursor: pointer;
	padding: 10px 0px;
	border-radius: 13px 13px 0 0;
	&:hover {
		background: #f2f3f3;
	}
	&:active {
		background: #ebecee;
	}
}
.rotate {
	transform: rotate(90deg);
	transition: var(--transition-time);
}
</style>
